import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { AritoForm, AritoHeaderTabs } from '@/components/custom/erp';
import { handleUpdateRowFields } from '../../utils/calc-util';
import { useInputTableRows } from './hooks/useDetailRows';
import { useAuth } from '@/contexts/auth-context';
import { initialFormValues } from '../../schema';
import { useFormFieldState } from '../../hooks';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { FormMode } from '@/types/form';
import { DetailTab } from './DetailTab';
import { BottomBar } from './BottomBar';
import { useTaxRows } from './hooks';
import { TaxTab } from './TaxTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: any;
  onSubmit?: (data: any) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('info');
  const { entityUnit } = useAuth();
  const { state, actions } = useFormFieldState(initialData);
  const {
    rows: detailRows,
    selectedRowUuid: detailSelectedRowUuid,
    handleRowClick: detailHandleRowClick,
    handleCellValueChange: detailHandleCellValueChange,
    populateFromChungTuChiTiet
  } = useInputTableRows(initialData?.chi_tiet_data || [], undefined, handleUpdateRowFields, state);
  const {
    rows: taxRows,
    selectedRowUuid: taxSelectedRowUuid,
    handleRowClick: taxHandleRowClick,
    handleAddRow: taxHandleAddRow,
    handleDeleteRow: taxHandleDeleteRow,
    handleCopyRow: taxHandleCopyRow,
    handlePasteRow: taxHandlePasteRow,
    handleMoveRow: taxHandleMoveRow,
    handleCellValueChange: taxHandleCellValueChange
  } = useTaxRows(initialData?.thue_data || []);

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
      setActiveTab('info');
    }
  }, [open]);

  const detail = useMemo(() => {
    return initialData?.chi_tiet || detailRows;
  }, [initialData, detailRows]);

  const { totalPSNo, totalPSCo } = useMemo(() => {
    return calculateTotals(detail);
  }, [detail]);

  const handlePopulateDetailRows = useCallback(
    (data: any[]) => {
      populateFromChungTuChiTiet(data);
    },
    [populateFromChungTuChiTiet]
  );

  const handleSubmit = async (data: any) => {
    const formData = transformFormData(data, state, totalPSNo, totalPSCo, detailRows, taxRows);

    try {
      if (formMode === 'edit') {
        await onSubmit?.({
          ...formData,
          unit_id: entityUnit?.uuid,
          uuid: initialData?.uuid
        });
      } else {
        await onSubmit?.({
          ...formData,
          unit_id: entityUnit?.uuid
        });
      }
      setIsFormDirty(false);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  return (
    <>
      <AritoForm
        mode={formMode}
        initialData={(() => {
          let data = initialData || initialFormValues;
          if (data && typeof data === 'object') {
            data = { ...data };
          }
          return data;
        })()}
        title={title}
        actionButtons={actionButtons}
        subTitle='Bút toán hủy/đảo'
        onSubmit={handleSubmit}
        onClose={handleClose}
        headerFields={
          <div onChange={() => setIsFormDirty(true)}>
            <AritoHeaderTabs
              tabs={[
                {
                  id: 'info',
                  label: 'Thông tin',
                  component: (
                    <BasicInfoTab
                      formMode={formMode}
                      formState={{ state, actions }}
                      onPopulateDetailRows={handlePopulateDetailRows}
                    />
                  )
                }
              ]}
              onTabChange={handleTabChange}
              defaultTabIndex={activeTab === 'info' ? 0 : 0}
              actionButtons={
                formMode !== 'add' && <InfoDropdown uuid={initialData?.uuid} ma_ct={initialData?.chung_tu} />
              }
            />
          </div>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  onRowClick={detailHandleRowClick}
                  onExport={() => console.log('Export clicked')}
                  onPin={() => console.log('Pin clicked')}
                />
              )
            },
            {
              id: 'tax',
              label: 'Thuế',
              component: (
                <TaxTab
                  formMode={formMode}
                  rows={taxRows}
                  selectedRowUuid={taxSelectedRowUuid}
                  onRowClick={taxHandleRowClick}
                  onAddRow={taxHandleAddRow}
                  onDeleteRow={taxHandleDeleteRow}
                  onCopyRow={taxHandleCopyRow}
                  onPasteRow={taxHandlePasteRow}
                  onMoveRow={taxHandleMoveRow}
                  onCellValueChange={taxHandleCellValueChange}
                />
              )
            }
          ]
        }
        bottomBar={activeTab === 'info' && <BottomBar totalPSNo={totalPSNo} totalPSCo={totalPSCo} state={state} />}
      />
    </>
  );
};

export default FormDialog;
